import { useCallback, useState } from 'react';
import type { CondimentQty, CondimentSaleDTO } from '@six-pockets/shared';
import { CloseIcon, PencilIcon, PlusIcon, SearchIcon } from '@/components/icons';
import { Button, InlineAlert, Section, TextField } from '@/components/ui';
import { useTablesData } from '@/context/TablesContext';
import { CondimentStepperList } from '@/features/condiments/components/CondimentStepperList';
import { itemsSummary, toItemLines, toItemsPayload, toQtyMap, type QtyMap } from '@/features/condiments/utils';
import { cx } from '@/utils/cx';
import { formatClock, formatINR } from '@/utils/format';
import type { BillForm } from '../useBillForm';
import styles from '@/styles/components/BillTabsEditor/style.scss';

/**
 * Step 1 of the bill, after the bookings: the condiment tabs, in two sections.
 * - Condiments on this bill (once there is one): the tabs on the bill and their total. ✎ opens a tab's editor above
 *   the list, to add missed items (saved to the tab straight away).
 * - Condiments: New tab (its form opens right under the button), and a short searchable list of other open tabs.
 */
export function BillTabsEditor({ form }: { form: BillForm }) {
  const [query, setQuery] = useState('');
  const { sales, selectedSales, takenSaleIds, editingTab, setEditingTab } = form;

  // Tabs on any of the bills (after Split, a tab goes on one bill only) are not offered again.
  const q = query.trim().toLowerCase();
  const others = (sales ?? []).filter((s) => !takenSaleIds.has(s.id) && (!q || s.tabName.toLowerCase().includes(q)));
  const othersCount = (sales ?? []).filter((s) => !takenSaleIds.has(s.id)).length;
  const busy = editingTab !== null;
  const editingSale = selectedSales.find((s) => s.id === editingTab) ?? null;
  const listed = selectedSales.filter((s) => s !== editingSale);

  return (
    <>
      {selectedSales.length > 0 && (
        <Section title="Condiments on this bill">
          <div className={styles.onBill}>
            {/* The editor is taller than the 4-row list, so it opens above it (the edited tab's row is hidden meanwhile). */}
            {editingSale && <TabEditor key={editingSale.id} sale={editingSale} form={form} />}
            {listed.length > 0 && (
              <ul className={cx(styles.list, styles.scroll)}>
                {listed.map((s) => (
                  <TabRow key={s.id} sale={s} selected>
                    <Button size="sm" variant="ghost" iconOnly icon={<PencilIcon size={14} />} aria-label={`Add items to ${s.tabName}'s tab`} title="Add or change items" onClick={() => setEditingTab(s.id)} disabled={busy} />
                    <Button size="sm" variant="ghost" iconOnly icon={<CloseIcon size={14} />} aria-label={`Take ${s.tabName}'s tab off the bill`} title="Remove from bill" onClick={() => form.toggleSale(s.id)} disabled={busy} />
                  </TabRow>
                ))}
              </ul>
            )}
            <div className={styles.totalRow}>
              <span>
                Condiments · {selectedSales.length} {selectedSales.length === 1 ? 'tab' : 'tabs'}
              </span>
              <span className={styles.money}>{formatINR(form.totals.condimentsTotal)}</span>
            </div>
          </div>
        </Section>
      )}

      <Section
        title="Condiments"
        action={
          <Button size="sm" variant="outline" icon={<PlusIcon size={14} />} onClick={() => setEditingTab('new')} disabled={!sales || busy}>
            New tab
          </Button>
        }
      >
        {form.salesError ? (
          <InlineAlert message={form.salesError} />
        ) : !sales ? (
          <span className={styles.muted}>Loading condiment tabs…</span>
        ) : editingTab === 'new' ? (
          // The New tab form takes the place of the open tabs until it is saved or cancelled.
          <TabEditor sale={null} form={form} />
        ) : othersCount > 0 ? (
          <div className={styles.picker}>
            <TextField
              size="md"
              aria-label="Find a condiment tab to add"
              placeholder={`Add an open tab · ${othersCount} open`}
              prefix={<SearchIcon size={16} />}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {others.length === 0 ? (
              <span className={styles.muted}>No tabs match “{query.trim()}”</span>
            ) : (
              <ul className={cx(styles.list, styles.scroll)}>
                {others.map((s) => (
                  <TabRow key={s.id} sale={s} selected={false}>
                    <Button size="sm" variant="outline" icon={<PlusIcon size={14} />} aria-label={`Add ${s.tabName}'s tab to the bill`} onClick={() => form.toggleSale(s.id)} disabled={busy}>
                      Add
                    </Button>
                  </TabRow>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <span className={styles.muted}>{selectedSales.length > 0 ? 'No other open tabs.' : 'No open condiment tabs.'} Use New tab to add one.</span>
        )}
      </Section>
    </>
  );
}

/** One tab: name, time and items, its total, and the actions passed in. */
function TabRow({ sale, selected, children }: { sale: CondimentSaleDTO; selected: boolean; children: React.ReactNode }) {
  return (
    <li className={cx(styles.row, selected && styles.selected)}>
      <span className={styles.main}>
        <span className={styles.label}>
          {sale.tabName}
          <span className={styles.meta}> · {formatClock(sale.createdAt)}</span>
        </span>
        <span className={styles.items} title={itemsSummary(sale.items)}>
          {itemsSummary(sale.items)}
        </span>
      </span>
      <span className={styles.money}>{formatINR(sale.total)}</span>
      <span className={styles.actions}>{children}</span>
    </li>
  );
}

/** Inline editor: add missed items to a tab on the bill, or open a new tab (sale = null). */
function TabEditor({ sale, form }: { sale: CondimentSaleDTO | null; form: BillForm }) {
  const { condiments } = useTablesData();
  const [name, setName] = useState('');
  const [qty, setQty] = useState<QtyMap>(() => toQtyMap(sale?.items ?? []));
  const setOne = useCallback((id: string, n: number) => setQty((q) => ({ ...q, [id]: n })), []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lines = toItemLines(qty, condiments, sale?.items ?? []);
  const total = lines.reduce((a, l) => a + l.amount, 0);
  const blocked = !sale && !name.trim() ? 'Enter a tab name' : lines.length === 0 ? 'Add at least one condiment' : undefined;

  const save = async () => {
    if (blocked || saving) return;
    setSaving(true);
    setError(null);
    try {
      const items: CondimentQty[] = toItemsPayload(qty);
      if (sale) await form.saveTab(sale.id, items);
      else await form.createTab(name.trim(), items);
      form.setEditingTab(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the tab');
      setSaving(false);
    }
  };

  return (
    <div className={styles.editor}>
      <div className={styles.editorHead}>
        <span className={styles.label}>{sale ? `${sale.tabName}'s tab` : 'New tab'}</span>
        <span className={styles.money}>{formatINR(total)}</span>
      </div>
      {error && <InlineAlert message={error} />}
      {!sale && (
        <TextField size="md" label="Tab name" placeholder="e.g. Rahul or Rahul's friend" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} autoFocus />
      )}
      <div className={styles.steppers}>
        <CondimentStepperList condiments={condiments} qty={qty} onChange={setOne} showPrice layout="grid" />
      </div>
      <div className={styles.editorFoot}>
        <Button size="sm" variant="secondary" onClick={() => form.setEditingTab(null)} disabled={saving}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" onClick={save} disabled={saving || !!blocked} title={blocked}>
          {saving ? 'Saving…' : sale ? 'Save tab' : 'Add tab to bill'}
        </Button>
      </div>
    </div>
  );
}
